<template>
    <div id="suppliers">
        <div class="row action-bar">
            <div class="col col-12 col-xxxxl-6">
                <div class="input-group filter-input">
                    <span class="input-group-text" id="basic-addon3"><b-icon icon="search" /></span>
                    <input
                        class="form-control"
                        v-model="filter.q"
                        :debounce="filterDebounce"
                        :placeholder="$t('overview.suppliers.table.search.placeholder')" />
                </div>
            </div>

            <div class="col col-12 col-xxxxl-6">
                <div class="filter" @click="filterExpanded = !filterExpanded">
                    <small>
                        <span class="text-muted">{{ $t("overview.suppliers.table.filter.label") }}</span>
                    </small>
                    <AnimatedCaret :expanded="filterExpanded" class="ms-1"/>
                </div>

                <div class="dropdown d-inline me-4">
                    <button class="btn btn-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                        <small>
                            <span class="text-muted">{{ $t("ordering.label") }}: </span>
                            <span class="bold">{{ selectedSortOption.text }}</span>
                        </small>
                        <AnimatedCaret :expanded="orderingExpanded" class="ms-1"/>
                    </button>
                    <ul class="dropdown-menu">
                        <li
                            v-for="(n, i) in sortOptions"
                            class="dropdown-link"
                            :key="'opt-sort-' + i"
                            :active="selectedSortOptionValue == i"
                            @click="selectedSortOptionValue = i"
                        >
                            <span class="dropdown-item">{{ n.text }}</span>
                        </li>
                    </ul>
                </div>

                <div class="dropdown d-inline me-4">
                    <button
                        class="btn btn-secondary dropdown-toggle"
                        type="button"
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="outside"
                        aria-expanded="false">
                        <small>
                            <span class="text-muted">{{ $t("overview.suppliers.table.fields.label") }}</span>
                        </small>
                        <AnimatedCaret :expanded="fieldsExpanded" class="ms-1"/>
                    </button>
                    <ul class="dropdown-menu">
                        <li
                            v-for="(n, i) in fieldsOptions"
                            class="dropdown-link"
                            :key="'opt-field-' + i + 1"

                        >
                            <span class="dropdown-item">
                                <div class="form-check form-switch">
                                    <label class="form-check-label" for="flexSwitchCheckDefault" v-html="n.label"></label>
                                    <input
                                        class="form-check-input"
                                        v-model="n.isActive"
                                        :disabled="!isFieldEnabled(n)"
                                        type="checkbox"
                                        role="switch"
                                        id="flexSwitchCheckDefault"
                                    >
                                </div>
                            </span>
                        </li>
                    </ul>
                </div>

                <template v-if="showTreeMap">
                    <button v-on:click="showTreeMap = !showTreeMap" class="treemap-btn">
                        <small>
                            <b-icon icon="eye-slash" font-scale="1.2" class="me-2" />
                            <span> {{ $t('overview.tenders.table.treemap.hide') }}</span>
                        </small>
                    </button>
                </template>
                <template v-else>
                    <button v-on:click="showTreeMap = !showTreeMap" class="treemap-btn">
                        <small>
                            <b-icon icon="eye" font-scale="1.2" class="me-2" />
                            <span> {{ $t('overview.tenders.table.treemap.show') }}</span>
                        </small>
                    </button>
                </template>
            </div>
        </div>

        <div v-show="filterExpanded" class="other-filters mb-5">
            <div class="row text-start">
                <div class="col-12 col-xl-6 mb-3 mb-xl-0">
                    <h5>
                        {{ $t("overview.suppliers.table.filter.flags.label") }}
                    </h5>

                    <MultiselectFilter :type="'supplier'" :version="version" :hash="hash"
                        @flags="(flags) => { filter.flag = flags }">
                        <template #option="props">
                            <template v-if="props.option.$isLabel">
                                <strong>{{ props.option.$groupLabel }}</strong>
                            </template>
                            <template v-else>
                                <div class="d-flex justify-content-between">
                                    <span>{{ props.option.name }}</span>
                                    <span class="text-muted text-end ml-4">{{ props.option.count }}</span>
                                </div>
                            </template>
                        </template>
                    </MultiselectFilter>
                </div>

                <div class="col-6 col-xl-3">
                    <h5>{{ $t("overview.suppliers.table.head.risk") }}</h5>
                    <div class="form-check text-start">
                        <input class="form-check-input" v-model="filter.risk" type="checkbox" value="high" id="risk_check_high">
                        <label class="form-check-label" for="risk_check_high">
                            <RiskLevelDumpling level="high" />&nbsp;{{ $t("risk.levelFull.high") }}
                        </label>
                    </div>
                    <div class="form-check">
                        <input class="form-check-input" v-model="filter.risk" type="checkbox" value="medium" id="risk_check_medium">
                        <label class="form-check-label" for="risk_check_medium">
                            <RiskLevelDumpling level="medium" />&nbsp;{{ $t("risk.levelFull.medium") }}
                        </label>
                    </div>
                    <div class="form-check">
                        <input class="form-check-input" v-model="filter.risk" type="checkbox" value="low" id="risk_check_low">
                        <label class="form-check-label" for="risk_check_low">
                            <RiskLevelDumpling level="low" />&nbsp;{{ $t("risk.levelFull.low") }}
                        </label>
                    </div>
                    <div class="form-check">
                        <input class="form-check-input" v-model="filter.risk" type="checkbox" value="no_risk" id="risk_check_no">
                        <label class="form-check-label" for="risk_check_no">
                            <RiskLevelDumpling level="no" />&nbsp;{{ $t("risk.levelFull.no") }}
                        </label>
                    </div>
                </div>

                <div class="col-6 col-xl-3">
                    <h5>
                        {{ $t("overview.suppliers.table.filter.date.label") }}
                    </h5>

                    <template
                        v-for="(g, i) in chunk(range(analysis.suppliers_min_year, analysis.suppliers_max_year), 4)"
                        :key="`group-${i}`">
                        <div v-for="y in g" :key="y" class="form-check text-start">
                            <input class="form-check-input" v-model="filter.year" type="checkbox" :value="y" :id="`year_check_${y}`">
                            <label class="form-check-label" :for="`year_check_${y}`">
                                {{ y }}
                            </label>
                        </div>
                    </template>
                </div>
            </div>
        </div>


        <TreeMap ref="tree_map_data_table" :version="version" :title="$t('overview.suppliers.table.treemap.title')"
            :show="showTreeMap" :hash="hash" :type="'supplier'" :filter="filter" :url_type="'supplier'"
            :order="selectedSortOption" />
        <Table
            :columns="selectedFields"
            :itemsDataProvider="dataProvider"
            :sort-by="selectedSortOption.value.by"
            @update:sort-by="selectedSortOption.value.by = $event"
            :sort-desc="selectedSortOption.value.desc"
            @update:sort-desc="selectedSortOption.value.desc = $event"
            :per-page="perPage"
            :start-page="startPage"
            id="suppliers-data-table"
            :link="getSupplierRoute"
            @row-clicked="(item) => $router.push(getSupplierRoute(item))" ref="suppliers_data_table">
            <template v-slot:data_total_sum="data">
                <span class="numeric">{{ formatNumber(data.item.total_sum) }}</span>
            </template>

            <template v-slot:data_bid_sum="data">
                <span class="numeric">{{ formatNumber(data.item.bid_sum) }}</span>
            </template>

            <template v-slot:data_accounting_payments="data">
                <span class="numeric">{{ formatNumber(data.item.accounting_payments) }}</span>
            </template>

            <template v-slot:data_risk="data">
                <RiskLevelDumpling :value="data.item.risk" :level="versionRiskLevel(data.item, version)" />
            </template>
        </Table>
    </div>
</template>

<script>
import AnimatedCaret from "@/components/AnimatedCaret.vue";
import RiskLevelDumpling from "@/components/RiskLevelDumpling.vue";
import MultiselectFilter from "@/components/MultiselectFilter.vue";
import Table from "@/components/Table.vue";
import TreeMap from "@/components/TreeMap.vue";
import riskMixin from "@/plugins/riskMixin.js";
import formattersMixin from "@/plugins/formattersMixin.js";
import dataTableMixin from "@/plugins/dataTableMixin.js";
import analysisMixin from "@/plugins/analysisMixin.js";

export default {
    name: "suppliers-table",
    props: {
        initFilter: { type: Object, required: false, default: () => null },
        startPage: { type: Number, required: false, default: 1 },
        perPage: { type: Number, required: false, default: 20 },
        version: { required: true },
        hash: { required: true }
    },
    components: { AnimatedCaret, RiskLevelDumpling, MultiselectFilter, Table, TreeMap },
    mixins: [riskMixin, formattersMixin, dataTableMixin, analysisMixin],
    data: function () {
        return {
            isBusy: false,
            initialized: false,
            showTreeMap: true,
            selectedSortOptionValue: 0,
            filter: {},
            fields: [
                {
                    key: "risk",
                    label: this.$t("overview.suppliers.table.head.risk"),
                    thClass: "text-start text-nowrap",
                    class: "text-center",
                    isActive: true
                },
                {
                    key: "ico",
                    label: this.$t("organization_id").toUpperCase(),
                    class: "text-start",
                    isActive: true
                },
                {
                    key: "name",
                    label: this.$t("overview.suppliers.table.head.name"),
                    class: "text-start absorbing-column text-xl-nowrap",
                    isActive: true
                },
                {
                    key: "total_sum",
                    label: this.$t("overview.suppliers.table.head.priceFull"),
                    class: "text-end text-xxl-nowrap",
                    isActive: true,
                    requiredVersion: "full"
                },
                {
                    key: "bid_sum",
                    label: this.$t("overview.suppliers.table.head.priceLite"),
                    class: "text-end text-xxl-nowrap",
                    isActive: true,
                    requiredVersion: "lite"
                },
                {
                    key: "department",
                    label: this.$t("overview.suppliers.table.head.department"),
                    class: "text-end",
                    isActive: false,
                    requiredVersion: "full"
                }
            ],
            filterTimeout: null,
            filterExpanded: false,
            orderingExpanded: false,
            fieldsExpanded: false,
            flagsExpanded: false
        };
    },
    created: function () {
        this.filter = { ...this.defaultFilter };
        if (this.initFilter) {
            this.filter = { ...this.filter, ...this.initFilter };
            this.filterExpanded = true;
        }
    },
    mounted: function () {
        var data = this.getLocalStrogeMenu();
        if (data) {
            this.fields = data.fields;
        }
        else {
            this.setLocalStorageMenu(true);
        }
    },
    watch: {
        filter: {
            deep: true,
            handler: function (value) {
                if (this.filterTimeout !== null) {
                    clearTimeout(this.filterTimeout);
                }
                // first reuqest of data table provider sets property to TRUE
                if (this.initialized) {
                    this.filterTimeout = setTimeout(() => this.submitFilter(), this.filterDebounce);
                }

                this.$store.commit("setSupplierTableFilter", value);
            }
        }
    },
    computed: {
        analysis: function () {
            return this.$store.state.analysis;
        },
        tableSettings: function () {
            return {
                borderless: true,
                noSortReset: true,
                showEmpty: true,
                items: this.dataProvider,
                fields: this.selectedFields,
                filter: this.filter.q,
                busy: this.isBusy,
                emptyText: this.$t("table.empty"),
                emptyFilteredText: this.$t("table.filter.empty"),
                labelSortAsc: this.$t("table.sort.asc"),
                labelSortDesc: this.$t("table.sort.asc"),
                labelSortClear: this.$t("table.sort.asc"),
                sortNullLast: false,
                responsive: "xl",
                tbodyTrClass: "clickable"
            };
        },
        selectedFields: function () {
            return this.fieldsOptions.filter((n) => n.isActive);
        },
        fieldsOptions: function () {
            var fields = this.fields.filter((n) => this.verifyVersion(this.version, n.requiredVersion));

            if (this.analysis.start_date_invoice == null) {
                fields = fields.filter((n) => n.key != "accounting_payments");
            }
            return fields;
        },
        sortOptions: function () {
            return [
                {
                    text: this.$t("ordering.options.price.desc"),
                    value: { by: "total_sum", desc: true }
                },
                {
                    text: this.$t("ordering.options.risk.desc"),
                    value: { by: "risk", desc: true }
                },
                {
                    text: this.$t("ordering.options.alphabet.asc"),
                    value: { by: "name", desc: false }
                }
            ];
        },
        selectedSortOption: function () {
            return this.sortOptions[this.selectedSortOptionValue];
        },
        fieldsLimit: () => 3,
        pageOptions: function () {
            return [10, 20, 50];
        },
        defaultFilter: function () {
            return {
                q: "",
                risk: [],
                flag: [],
                year: []
            };
        },
        filterDebounce: () => 700,
        datepickerSettings: function () {
            return {
                size: "sm",
                resetButton: true,
                startWeekday: 1,
                class: "filter-input",
                locale: "cs",
                dateFormatOptions: {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric"
                },
                labelPrevDecade: this.$t("datepicker.labelPrevDecade"),
                labelPrevYear: this.$t("datepicker.labelPrevYear"),
                labelPrevMonth: this.$t("datepicker.labelPrevMonth"),
                labelCurrentMonth: this.$t("datepicker.labelCurrentMonth"),
                labelNextMonth: this.$t("datepicker.labelNextMonth"),
                labelNextYear: this.$t("datepicker.labelNextYear"),
                labelNextDecade: this.$t("datepicker.labelNextDecade"),
                labelToday: this.$t("datepicker.labelToday"),
                labelSelected: this.$t("datepicker.labelSelected"),
                labelNoDateSelected: this.$t("datepicker.labelNoDateSelected"),
                labelCalendar: this.$t("datepicker.labelCalendar"),
                labelNav: this.$t("datepicker.labelNav"),
                labelHelp: this.$t("datepicker.labelHelp"),
                labelTodayButton: this.$t("datepicker.labelTodayButton"),
                labelResetButton: this.$t("datepicker.labelResetButton"),
                labelCloseButton: this.$t("datepicker.labelCloseButton")
            };
        }
    },
    methods: {
        dataProvider: function (ctx) {
            var params = this.initRequestParams(ctx);

            this.$store.commit("setSupplierTablePage", ctx.currentPage);
            this.$store.commit("setSupplierTablePerPage", ctx.perPage);

            params[`${this.version}_hash`] = this.hash;

            if (this.filter.risk) {
                var risks = "";
                var levels = ["high", "medium", "low", "no_risk"];
                levels.forEach((n) => {
                    if (this.filter.risk.indexOf(n) >= 0) {
                        if (risks != "") {
                            risks += ",";
                        }
                        risks += n;
                    }
                });
                if (risks.length > 0) {
                    var risk = { "risks": risks };
                    params = { ...params, ...risk };
                }
            }

            if (this.filter.flag && this.filter.flag.length) {
                params = {
                    ...params,
                    ...{ flag: this.filter.flag.map((f) => f.name).join(",") }
                };
            }
            if (this.filter.year && this.filter.year.length) {
                params = { ...params, ...{ year: this.filter.year.join(",") } };
            }

            if (this.filter.q) {
                params = {...params, ...{q: this.filter.q}}
            }

            params["order_by"] += ",name";

            return this.$store.getters.api
                .get("supplier/", { params: params })
                .then((response) => {
                    this.$refs.suppliers_data_table.setTotalRows(response.data.count);
                    return response.data.data;
                })
                .catch((e) => {
                    console.dir(e);
                    return [];
                })
                .finally(() => (this.initialized = true));
        },
        submitFilter: function () {
            this.$refs.tree_map_data_table.refresh();
            this.$refs.suppliers_data_table.refresh();
        },
        isFieldEnabled: function (field) {
            return this.selectedFields.length > this.fieldsLimit || !field.isActive;
        },
        setFilter: function (patch) {
            this.filter = { ...patch };
        },
        updateFilter: function (patch) {
            this.filter = { ...this.filter, ...patch };
        },
        resetFilter: function () {
            this.filter = { ...this.defaultFilter };
        },
        toggleField: function (field) {
            if (this.isFieldEnabled(field)) {
                field.isActive = !field.isActive;
                this.setLocalStorageMenu(false);
            }
        },
        range: function (start, stop, step = 1) {
            return Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);
        },
        chunk: function (arr, n) {
            if (!arr) {
                return [];
            }

            return Array(Math.ceil(arr.length / n))
                .fill()
                .map((_, i) => arr.slice(i * n, i * n + n));
        },
        getSupplierRoute: function (supplier) {
            return {
                name: 'supplier-detail',
                params: { version: this.version, hash: this.hash, slug: supplier.slug }
            }
        },
        setLocalStorageMenu: function (default_by_user) {
            localStorage.setItem('suppliersMenu', JSON.stringify({ default: default_by_user, fields: this.fields }));
        },
        getLocalStrogeMenu: function () {
            return JSON.parse(localStorage.getItem('suppliersMenu'))
        }
    }
};
</script>

<style scoped lang="scss">
@use "@/scss/custom";

#suppliers {
    text-align: left;
    .filter-input {
        border: none;
        background-color: custom.$light-grey;

        &.input-group {

            input,
            .input-group-text {
                border: none;
                background-color: custom.$light-grey;
            }
        }
    }

    .action-bar {
        margin-bottom: 20px;
        text-align: left;

        .filter,
        .ordering,
        .fields,
        .treemap-btn {
            white-space: nowrap;
            display: inline-block;
            line-height: 38px;
            vertical-align: unset;
            cursor: pointer;

            .animated-caret {
                margin-left: 10px;
            }

            &:not(:last-child) {
                margin-right: 20px;
            }
        }

        .treemap-btn {
            background-color: rgba(0, 0, 0, 0);
            margin: 0px;
            padding: 0;
            border: none;

            svg {
                margin-left: 0px;
            }
        }

        .ordering {
            width: fit-content;
        }
    }

    .other-filters {
        h5 {
            color: custom.$text-muted;
            font-size: custom.$small-font-size;
            font-weight: normal;
            margin-bottom: 10px;
        }
    }

    #page-size-select-group {
        width: 180px;
    }
}
</style>

<style lang="scss">
.custom-checkbox,
.custom-switch {

    input[type="checkbox"],
    label,
    label::after,
    label::before {
        cursor: pointer;
    }
}

#suppliers {
    .filter-input {
        &.b-form-datepicker {
            label {
                line-height: 32px;
            }
        }
    }

    .dropdown {
        .dropdown-toggle {
            padding: 0;
            background-color: transparent;
            border: none;
            vertical-align: unset;

            &:hover {
                background-color: transparent;
            }
        }

        .dropdown-link {
            a {
                text-decoration: none;
            }
        }
    }
}
</style>
